/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import {
    shape, arrayOf, string, bool, func,
} from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import { useUIDSeed } from 'react-uid';

import TemplateMultiSubMainBanner from './TemplateMultiSubMainBanner';
import GraphqlBreadcrumbs from '../../GraphqlCommonComponents/GraphqlBreadcrumbs/GraphqlBreadcrumbs';
import OurFavoritesTemplate from './OurFavoritesTemplate';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import MoreSubscriptionsTemplate from './MoreSubscriptionsTemplate';
import SubscriptionBlock from './SubscriptionBlock';
import FAQBlockV2 from '../../GraphqlCommonComponents/FAQBlockV2/FAQBlockV2';
import InfoBlock from './InfoBlock';

// helpers

const useStyles = makeStyles((theme) => ({
    container: {
        [theme.breakpoints.down('sm')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downSmall,
            margin: '0 auto',
        },
        [theme.breakpoints.down('md')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downMedium,
            margin: '0 auto',
        },
        '& [class*="GraphqlBreadcrumbs-breadCrumb-"]': {
            padding: '10px 0',
        },
    },
    title: {
        fontSize: '26px',
        position: 'relative',
        textAlign: 'center',
        width: '100%',
        marginTop: '10px',
        '&::before': {
            content: '""',
            background: theme.palette?.colorNeutral60 || '#9E9E9E',
            width: '100%',
            height: '1px',
            position: 'absolute',
            right: 0,
            left: 0,
            top: '50%',
            zIndex: 1,
            [theme.breakpoints.down(600)]: {
                background: 'transparent',
            },
        },
        '& h2 ,& p': {
            fontSize: '26px',
            fontWeight: 'normal',
            position: 'relative',
            display: 'inline-block',
            zIndex: 9,
            background: '#FFF',
            padding: '0 30px',
            margin: '0 auto',
            fontFamily: 'ivypresto-text, PlayfairDisplayRegular, serif',
            textAlign: 'center',
        },
        '& a': {
            position: 'absolute',
            right: 0,
            top: 10,
            zIndex: 1,
        },
    },
    faqContainer: {
        margin: '0px 80px',
        [theme.breakpoints.down(600)]: {
            margin: '0px 10px',
        },
        '& ._main_faq_title': {
            fontFamily: 'ivypresto-text, PlayfairDisplayBold, serif',
            fontSize: '2rem',
            [theme.breakpoints.down(600)]: {
                fontSize: '1.8rem',
            },
        },
    },
    aboutSupContainer: {
        margin: '0px 180px',
        '& .MuiAccordionSummary-content': {
            fontWeight: '900',
        },
        [theme.breakpoints.down(600)]: {
            margin: '0px 50px',
        },
    },
    templateFareMain: {
        margin: '0px auto',
        maxWidth: 1400,
    },
    breadcrumb: {
        margin: '10px 0',
    },
}));

const LandingPageTemplate = ({
    templateData, handleTrackEvent,
}) => {
    const classes = useStyles();
    const categoryBlocks = templateData?.category_blocks || [];
    const seed = useUIDSeed();
    const brandName = useSelector(getBrandName);
    const showBreadcrumbs = templateData?.seo?.breadcrumb?.length > 0;
    const pageTrackingProps = templateData?.tracking || {};
    if (categoryBlocks.length) {
        return (
            <>
                <div className={classes.templateFareMain}>
                    <Grid container className={classes.container}>
                        {showBreadcrumbs && (<div className={classes.breadcrumb}><GraphqlBreadcrumbs breadCrumbArray={templateData.seo.breadcrumb} /></div>)}
                        {
                            categoryBlocks?.map((item) => {
                                const blockKey = Object.keys(item)[0];
                                switch (blockKey) {
                                    case 'main_banner':
                                    {
                                        return <TemplateMultiSubMainBanner handleTrackEvent={handleTrackEvent} item={item} tracking={pageTrackingProps} />;
                                    }
                                    case 'faqs':
                                    {
                                        return <div key={seed(item)} className={classes.faqContainer}><FAQBlockV2 handleTrackEvent={handleTrackEvent} currentPage="Subscriptions Experience" faqBlock={item.faqs} displayCsTitle /></div>;
                                    }
                                    case 'about_subscription':
                                    {
                                        return <div key={seed(item)} className={classes.aboutSupContainer}><FAQBlockV2 handleTrackEvent={handleTrackEvent} currentPage="Subscriptions Experience" faqBlock={item.about_subscription} displayCsTitle /></div>;
                                    }
                                    case 'info_block':
                                    {
                                        return <InfoBlock mapKey={seed(item)} handleTrackEvent={handleTrackEvent} item={item.info_block} />;
                                    }
                                    case 'start_subscription_block':
                                    {
                                        return <SubscriptionBlock handleTrackEvent={handleTrackEvent} item={item} />;
                                    }
                                    case 'more_subscriptions_block':
                                    {
                                        return <MoreSubscriptionsTemplate handleTrackEvent={handleTrackEvent} templateData={templateData} brandName={brandName} item={item} />;
                                    }
                                    case 'our_favorites_block':
                                    {
                                        return <OurFavoritesTemplate mapKey={seed(item)} handleTrackEvent={handleTrackEvent} templateData={templateData} brandName={brandName} item={item} />;
                                    }
                                    default:
                                        return null;
                                }
                            })
                        }
                    </Grid>
                </div>
            </>
        );
    }
    return null;
};

LandingPageTemplate.propTypes = {
    templateData: shape({
        seo: shape({
            breadcrumb: arrayOf(
                shape({
                    title: string.isRequired,
                    href: string.isRequired,
                }).isRequired,
            ),
            keywords: string,
            meta_description: string,
            seo_searchable: bool.isRequired,
            page_title: string,
        }).isRequired,
        category_blocks: arrayOf(
            shape({
                main_banner: shape({
                    link: shape({
                        href: string.isRequired,
                        title: string.isRequired,
                    }),
                    desktop_image: shape({
                        url: string.isRequired,
                    }),
                }),
                sub_banner: shape({
                    desktop_image: shape({
                        url: string.isRequired,
                        title: string.isRequired,
                    }),
                }),
                bottom_banners: shape({
                    title: string.isRequired,
                    banners: arrayOf({
                        link: shape({
                            href: string.isRequired,
                        }),
                        desktop_image: shape({
                            url: string.isRequired,
                            title: string.isRequired,
                        }),
                    }),
                }),
            }),
        ),
    }).isRequired,
    handleTrackEvent: func.isRequired,
};

export default LandingPageTemplate;
